<template>
  <div
    class="stripe-payment-element"
    :class="{ 'stripe-payment-element--20504': treatment20504 }"
  >
    <div v-if="wait.is('Loading')" class="wait-overflow">
      <loading-spinner />
    </div>
    <!-- Ignore the unused `instance` variable.
      It is marked as "// attention: important part!" in vue-stripe-js docs
      -->
    <!-- eslint-disable  vue/no-unused-vars -->
    <StripeElements
      ref="stripeElementsRef"
      :key="`${remountStripeElements}`"
      :stripe-key="STRIPE_PUBLIC_KEY"
      :instance-options="instanceOptions"
      :elements-options="elementsOptions"
      class="stripe-elements"
    >
      <div
        :class="{
          'stripe-payment-element__express-framed-20504':
            expressCheckoutWalletsAvailable,
          'stripe-payment-element__express-framed-20504--multiple':
            expressCheckoutMultipleWallets,
        }"
      >
        <p
          v-if="expressCheckoutWalletsAvailable"
          class="stripe-payment-element__express-label-20504"
        >
          Express checkout
        </p>
        <StripeElement
          id="express-checkout-element"
          :key="`${stripeTheme}`"
          ref="expressCheckoutRef"
          :type="'expressCheckout'"
          :options="expressCheckoutOptionsForTheme(stripeTheme)"
          @ready="onExpressCheckoutReady"
          @change="changedHandler"
          @click="expressCheckoutClick"
          @loaderror="stripeElementLoadErrorHandler"
        />
      </div>
      <div
        v-if="expressCheckoutWalletsAvailable"
        class="stripe-payment-element__card-divider-20504"
      >
        <span>or pay with card</span>
      </div>
      <div class="stripe-payment-element__card-box">
        <StripeElement
          id="payment-element"
          ref="paymentElementRef"
          :type="'payment'"
          :options="paymentElementOptions"
          @ready="paymentElementReady = true"
          @change="changedHandler"
          @loaderror="stripeElementLoadErrorHandler"
        />
        <!--
          For components that use this component, so they can put submit/skip
          buttons here to render them inside the same box as the fields.
          https://github.com/shortformhq/main/issues/20192
        -->
        <slot name="actions" />
      </div>
    </StripeElements>
    <!-- eslint-enable  vue/no-unused-vars -->
  </div>
  <FormError
    :class="validation.stateClass('card') === 'is-invalid' ? 'd-block' : ''"
    :errors="validation.errors"
    field="card"
  />
  <button
    v-if="showButton"
    class="btn btn-primary mt-3"
    :disabled="!complete || saving"
    @click="save"
  >
    Save Card
  </button>
  <div v-show="savedAlert" class="alert alert-success">Card Saved</div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  watch,
  onBeforeUnmount,
  onErrorCaptured,
  ref,
} from 'vue'

// The vue-stripe-js package doesn't have typing
// @ts-ignore
import { StripeElements, StripeElement } from 'vue-stripe-js'
import type {
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'

import { STRIPE_PUBLIC_KEY } from '@/init/settings'
import { ValidationState } from '@/helpers/form'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import type { BillingView } from '@/models/billing'
import FormError from '@/components/ui/FormError.vue'

import { getEmail } from '@/services/auth'
import { useWait } from '@/app/composables/useWait'
import { emitter } from '@/services/mitt'
import { nightMode } from '@/services/night'
import { checkoutModalSimplification20504Variant } from '@/services/ab'

const props = withDefaults(
  defineProps<{
    billing: BillingView
    showButton?: boolean
  }>(),
  {
    showButton: true,
  },
)

const emit = defineEmits([
  'saved' as string,
  'elementsReady' as string,
  'loadError' as string,
])
const wait = useWait()
const treatment20504 = checkoutModalSimplification20504Variant() === 'B'

const stripeElementsRef = ref<typeof StripeElements | null>(null)
const expressCheckoutRef = ref<typeof StripeElement | null>(null)
const paymentElementRef = ref<typeof StripeElement | null>(null)

const validation = ref(new ValidationState())

// True once card data has been changed
const changed = ref(false)

// True once Stripe reports the the card is valid (but we din't save it yet).
const complete = ref(false)
// True while we are saving card data.
const saving = ref(false)
// True once we successfully saved card, triggers the success alert.
const saved = ref(false)

// This value is used as the `key` to the `StripeElements` component,
// so we can toggle it to remount the component.
const remountStripeElements = ref(false)
const stripeTheme = computed<'light' | 'dark'>(() =>
  nightMode.active ? 'dark' : 'light',
)

// The onboarding payment step hides its own chrome until both Stripe
// elements have finished loading.
const expressCheckoutReady = ref(false)
const paymentElementReady = ref(false)
const expressCheckoutWalletsAvailable = ref(false)
// Stripe shows "See more" once a second wallet overflows the single row.
const expressCheckoutMultipleWallets = ref(false)

const allVisibleElementsReady = computed(
  () => expressCheckoutReady.value && paymentElementReady.value,
)

function onExpressCheckoutReady(event?: {
  availablePaymentMethods?: Record<string, boolean> | null
}): void {
  expressCheckoutReady.value = true
  if (!treatment20504) {
    return
  }
  const methods = event?.availablePaymentMethods
  const availableWalletCount = methods
    ? Object.values(methods).filter(Boolean).length
    : 0
  expressCheckoutWalletsAvailable.value = availableWalletCount > 0
  expressCheckoutMultipleWallets.value = availableWalletCount > 1
}

watch(allVisibleElementsReady, (ready) => {
  if (ready) {
    emit('elementsReady')
  }
})

const setupIntent = ref<Record<string, any>>({})
// True once we confirmed the setupIntent
const setupIntentConfirmed = ref(false)
// One-shot flag indicating that Stripe wasn't available at mount time,
// so we should remount elements when Stripe reports it finished loading.
// https://github.com/shortformhq/main/issues/14547
let isWaitingForStripe = ref(false)

// https://stripe.com/docs/js/initializing#init_stripe_js-options
const instanceOptions = ref<Record<string, any>>({})
// https://stripe.com/docs/stripe.js#element-options
const paymentElementOptions = ref<StripePaymentElementOptions>({
  layout: {
    type: 'tabs',
    defaultCollapsed: false,
  },
  ...(treatment20504 ? { wallets: { link: 'never' } } : {}),
})

// https://stripe.com/docs/js/elements_object/create#stripe_elements-options
const elementsOptions = computed<StripeElementsOptionsMode>(() => ({
  mode: 'setup',
  currency: 'usd',
  appearance: stripeAppearance(stripeTheme.value),
}))

onBeforeMount(async () => {
  checkStripeLoadedOnMount()
  emitter.on('stripeFinishedLoading', remountWhenStripeLoads)

  // Pre-fill user's email for the Stripe Link payment
  const userEmail = getEmail()
  if (userEmail) {
    paymentElementOptions.value.defaultValues = {
      billingDetails: {
        email: userEmail,
      },
    }
  } else {
    Sentry.captureMessage('User email is null in the subscribe popup')
  }
  setupIntent.value = await backend.getSetupIntent()
})

watch(
  // @ts-ignore
  () => expressCheckoutRef.value?.stripeElement,
  (element) => {
    if (!element) {
      return
    }

    const handleReady = (): void => {
      element.addEventListener('confirm', expressCheckoutConfirm)
    }

    // When Stripe Element `on()` function becomes available, attach handler
    if (typeof element.on === 'function') {
      element.on('ready', handleReady)
    } else {
      // Fallback: bind immediately if already initialized (past the `ready` state)
      handleReady()
    }
  },
  { immediate: true },
)

onBeforeUnmount(async () => {
  // If the user didn't add payment data, cancel the setup intent
  if (!setupIntentConfirmed.value) {
    await backend.cancelSetupIntent(setupIntent.value)
  }
  emitter.off('stripeFinishedLoading', remountWhenStripeLoads)
})

/**
 * Capture any uncaught errors in this component and log to Sentry.
 *
 * We are not sure if we can catch any errors here, this is a test approach to see
 * if we log any errors to Stripe from this handler.
 */
onErrorCaptured((error) => {
  Sentry.captureException(error)
  return false
})

function changedHandler($event: any): void {
  changed.value = true
  complete.value = $event.complete
}

/**
 * Check if Stripe was loaded when the component mounted.
 *
 * Sets the `isWaitingForStripe` flag accordingly.
 */
function checkStripeLoadedOnMount(): void {
  isWaitingForStripe.value = window.Stripe === undefined
}

function stripeElementLoadErrorHandler(event: any): void {
  const error =
    event?.error instanceof Error
      ? event.error
      : new Error(event?.error?.message || 'Stripe element load error')

  Sentry.withScope((scope) => {
    scope.setTag('service', 'stripe')
    scope.setExtra('stripeLoadErrorEvent', event)
    Sentry.captureException(error)
  })

  // `elementsReady` never fires after this (the failed element never
  // reports `ready`) -- let consumers know so they can offer a way
  // forward instead of leaving the user stuck with no working UI.
  emit('loadError')
}

function stripeAppearance(
  theme: 'light' | 'dark',
): NonNullable<StripeElementsOptionsMode['appearance']> {
  const palette =
    theme === 'dark'
      ? {
          inputBorder: '#505258',
          inputBackground: '#1D1E20',
          tabBorder: '#505258',
          tabBackground: '#1D1E20',
          tabSelectedBorder: '#3876F0',
          tabSelectedBackground: 'rgba(56, 118, 240, 0.28)',
        }
      : {
          inputBorder: '#D5D8E0',
          inputBackground: undefined,
          tabBorder: '#D4D0D0',
          tabBackground: undefined,
          tabSelectedBorder: '#363B48',
          tabSelectedBackground: undefined,
        }

  const rules = {
    '.Input': {
      border: `1px solid ${palette.inputBorder}`,
      lineHeight: '20px',
      ...(palette.inputBackground
        ? { backgroundColor: palette.inputBackground }
        : {}),
      ...(treatment20504
        ? {
            borderRadius: '8px',
            padding: '12px 16px',
            ...(theme === 'light' ? { backgroundColor: '#F8F8F8' } : {}),
          }
        : {}),
    },
    '.Label': {
      fontWeight: '500',
      lineHeight: '18px',
      ...(treatment20504
        ? {
            fontSize: '16px',
            color: theme === 'dark' ? '#EEEFF0' : '#141C31',
          }
        : {}),
    },
    '.Tab': {
      border: `1px solid ${palette.tabBorder}`,
      ...(palette.tabBackground
        ? { backgroundColor: palette.tabBackground }
        : {}),
      ...(treatment20504 ? { borderRadius: '8px' } : {}),
    },
    '.Tab--selected': {
      border: `1px solid ${palette.tabSelectedBorder}`,
      ...(palette.tabSelectedBackground
        ? { backgroundColor: palette.tabSelectedBackground }
        : {}),
      ...(treatment20504 ? { borderRadius: '8px' } : {}),
    },
    '.TermsText': {
      fontSize: treatment20504 ? '11px' : '12px',
      ...(treatment20504
        ? {
            lineHeight: '18px',
            color: theme === 'dark' ? '#A7AAB3' : '#6B6D76',
          }
        : {}),
    },
  }

  return {
    // To style the Payment Element we use Stripe Appearance API
    // https://docs.stripe.com/elements/appearance-api
    theme: theme === 'dark' ? 'night' : 'stripe',
    variables: {
      ...(theme === 'dark'
        ? {
            colorPrimaryText: '#EEEFF0',
            colorText: '#EEEFF0',
            colorTextSecondary: '#A7AAB3',
            colorBackground: '#1D1E20',
            fontFamily: 'Rotunda, sans-serif',
            fontSizeBase: '16px',
          }
        : {
            colorPrimaryText: '#141C31',
            colorBackground: '#F8F8F8',
            fontFamily: 'Rotunda, sans-serif',
            fontSizeBase: '16px',
          }),
      ...(treatment20504
        ? {
            borderRadius: '24px',
            ...(theme === 'light' ? { colorBackground: '#FFFFFF' } : {}),
          }
        : {}),
    },
    rules: rules as NonNullable<
      NonNullable<StripeElementsOptionsMode['appearance']>['rules']
    >,
  }
}

function expressCheckoutOptionsForTheme(
  theme: 'light' | 'dark',
): StripeExpressCheckoutElementOptions {
  const blackButtons = treatment20504 || theme === 'dark'
  return {
    ...(treatment20504
      ? {
          buttonHeight: 48,
          layout: {
            maxColumns: 3,
            maxRows: 1,
          },
        }
      : {}),
    buttonType: {
      applePay: 'subscribe',
      googlePay: 'subscribe',
    },
    buttonTheme: {
      applePay: blackButtons ? 'black' : 'white-outline',
      googlePay: blackButtons ? 'black' : 'white',
    },
    paymentMethods: {
      applePay: 'always',
      googlePay: 'always',
    },
    paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
  }
}

function needsSaving(): boolean {
  return changed.value
}

async function paymentElementConfirmStripe(): Promise<any> {
  // @ts-ignore
  const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
  const params = {
    // @ts-ignore
    elements: stripeElementsRef.value.elements,
    clientSecret: setupIntent.value.client_secret,
    redirect: 'if_required',
    confirmParams: {
      return_url: 'https://www.shortform.com/app/billing',
    },
  }
  // @ts-ignore
  await stripeElementsRef.value.elements.submit()
  const { error } = await confirmSetupFn(params)
  if (error) {
    return { error: error }
  }

  setupIntentConfirmed.value = true
  return { success: true }
}

/**
 * Express Checkout Element click handler.
 *
 * We need to have the click handler for the element,
 * because we need to immediately call `event.resolve()` with `client_secret`.
 */
async function expressCheckoutClick(event: Event): Promise<any> {
  // @ts-ignore
  event.resolve(setupIntent.value.clientSecret)
}

/**
 * Express Checkout Element confirm handler.
 *
 * This method is called after the user goes through the payment option's
 * process: for example, chooses card, clicks confirm.
 */
async function expressCheckoutConfirm(): Promise<boolean> {
  if (saving.value) {
    return false
  }
  saving.value = true
  let loading = false

  try {
    const { error: submitError } =
      // @ts-ignore
      await stripeElementsRef.value.elements.submit()
    if (submitError) {
      handleStripeError(submitError)
      return false
    }

    // Create a ConfirmationToken using the details collected by the Express Checkout Element
    // @ts-ignore
    const { error, confirmationToken } =
      // @ts-ignore
      await stripeElementsRef.value.instance.createConfirmationToken({
        // @ts-ignore
        elements: stripeElementsRef.value.elements,
        params: {
          return_url: 'https://www.shortform.com/app/billing',
        },
      })

    if (error) {
      // From Stripe documentation:
      // This point is only reached if there's an immediate error when
      // confirming the payment. Show the error to your customer (for example, payment details incomplete)
      // https://docs.stripe.com/elements/express-checkout-element/accept-a-payment?client=html#create-ct
      handleStripeError(error)
      return false
    }

    // @ts-ignore
    const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
    const params = {
      clientSecret: setupIntent.value.client_secret,
      redirect: 'if_required',
      confirmParams: {
        confirmation_token: confirmationToken.id,
        return_url: 'https://www.shortform.com/app/billing',
      },
    }
    // @ts-ignore
    const { error: confirmationError, setupIntent: confirmedIntent } =
      await confirmSetupFn(params)
    if (confirmationError) {
      handleStripeError(confirmationError)
      return false
    }
    if (
      confirmedIntent?.status !== 'succeeded' ||
      confirmedIntent.id !== setupIntent.value.id
    ) {
      handleStripeError({
        message: 'Unable to confirm your payment method. Please try again.',
      })
      return false
    }
    setupIntentConfirmed.value = true
    wait.start('Loading')
    loading = true
    const billing = await backend.savePaymentMethod({
      id: setupIntent.value.id,
    })
    props.billing.card_data = billing.card_data
    _setSaved('')

    // There is a new billing card data returned by stripe,
    // if we saved it successfully, update props.billing,
    // so the new card is displayed.
    emit('saved')

    return true
  } catch (error) {
    if (loading) {
      _setSaved(error)
    } else {
      handleStripeError(error)
    }
    return false
  } finally {
    saving.value = false
    if (loading) {
      wait.end('Loading')
    }
  }
}

/**
 * Save card, returns true on success
 */
async function save(): Promise<boolean> {
  saving.value = true

  try {
    const data = await paymentElementConfirmStripe()
    if (data.error) {
      handleStripeError(data.error)
      return false
    }
    const billing = await backend.savePaymentMethod({
      id: setupIntent.value.id,
    })
    props.billing.card_data = billing.card_data
    _setSaved('')

    // There is a new billing card data returned by stripe,
    // if we saved it successfully, update props.billing,
    // so the new card is displayed.
    emit('saved')
    return true
  } catch (error) {
    _setSaved(error)
  }
  return false
}

/**
 * Update state flags depending on the error value.
 */
function _setSaved(error: any): void {
  if (error) {
    saving.value = false
    saved.value = false
    changed.value = true
    validation.value.showErrors(error)
  } else {
    saving.value = false
    saved.value = true
    changed.value = false
    validation.value.reset()
  }
}

function handleStripeError(error: any): void {
  // Something went wrong on Stripe side.
  const expected_errors = [
    'incomplete_zip',
    'incomplete_cvc',
    'incomplete_expiry',
    'incomplete_number',
    'invalid_number',
    'invalid_expiry_month_past',
  ]
  if (error.code && expected_errors.indexOf(error.code) === -1) {
    // Unexpected error, send to Sentry.
    Sentry.withScope((scope) => {
      scope.setExtra('stripeData', error)
      Sentry.captureMessage(error.message)
    })
  }
  _setSaved({
    response: {
      status: 400,
      data: { errors: { card: [error.message] } },
    },
  })
}

const savedAlert = computed(() => {
  if (saved.value) {
    // 3 seconds to hide the alert
    return 3
  }
  return false
})

/**
 * Remount Stripe Elements when Stripe finishes loading, if it was missing on mount.
 *
 * If Stripe was missing on mount, we set the `isWaitingForStripe` flag to true.
 * Once we receive the `stripeFinishedLoading` event, we check the flag and remount
 * the Stripe Elements if needed.
 */
function remountWhenStripeLoads(): void {
  // Stripe was loaded on component mount, no need to remount the Stripe Elements
  if (!isWaitingForStripe.value) {
    return
  }

  if (!window.Stripe) {
    // Stripe is still not available, log an error to Sentry.
    // We received the `stripeFinishedLoading` event, so this would be unexpected.
    Sentry.captureException(
      new Error(
        'StripeCard component: Stripe instance undefined despite receiving stripeFinishedLoading event',
      ),
    )
    isWaitingForStripe.value = false
    return
  }

  isWaitingForStripe.value = false
  if (!stripeElementsRef.value?.elements) {
    // Value is irrelevant here, we just need to change it to trigger a remount
    // of the StripeElements component
    remountStripeElements.value = !remountStripeElements.value
    expressCheckoutReady.value = false
    expressCheckoutWalletsAvailable.value = false
    expressCheckoutMultipleWallets.value = false
    paymentElementReady.value = false
  }
}

defineExpose({
  needsSaving,
  save,
})
</script>

<style lang="scss" scoped>
.stripe-payment-element {
  min-height: 345px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;

  :deep(.StripeElement) {
    height: 100% !important;
    margin-bottom: 20px;
    padding: 0;
  }

  &__express-framed-20504 {
    position: relative;
    margin-top: 12px;
    padding: 22px 16px;
    border: 1px solid $c-black-13;
    border-radius: 12px;

    // "See more" already fills the space under the wallet button.
    &--multiple {
      padding-bottom: 8px;
    }
  }

  &__express-label-20504 {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0 8px;
    background: $c-white;
    color: rgba($c-black-3, 0.8);
    font-size: 14px;
    line-height: 21px;
    text-align: center;
  }

  &__card-divider-20504 {
    display: flex;
    align-items: center;
    gap: 20px;
    margin: 24px 0 18px;
    color: rgba($c-black-3, 0.8);
    font-size: 14px;
    line-height: 21px;

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-top: 1px solid $c-black-13;
    }
  }

  &--20504 {
    min-height: 0;

    :deep(.StripeElement) {
      margin-bottom: 0;
    }

    :deep(#express-checkout-element) {
      height: auto !important;
      min-height: 48px;
      margin-bottom: 0 !important;
      padding: 0 !important;
    }

    :deep(#payment-element) {
      margin-bottom: 12px;
    }
  }
}

.wait-overflow {
  display: flex;
  position: absolute;
  top: 0;
  left: -6px;
  right: 0;
  bottom: 0;
  background-color: rgba($c-white, 0.85);
  z-index: 3;
  border-radius: 7px;
  align-items: center;
  justify-content: center;
}

:deep(.StripeElement) {
  box-sizing: border-box;
  height: 60px;
  padding: 20px 12px;
  border-radius: 8px;
}

:deep(.StripeElement--focus) {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

:deep(.StripeElement--invalid) {
  border-color: #fa755a;
}

:deep(.StripeElement--webkit-autofill) {
  background-color: #fefde5 !important;
}

:deep(.loading__spinner) {
  margin: 0;
}

.night-mode {
  .wait-overflow {
    background-color: rgba($c-bg-5, 0.85);
  }

  .stripe-payment-element__express-framed-20504 {
    border-color: $c-dark-5;
  }

  .stripe-payment-element__express-label-20504,
  .stripe-payment-element__card-divider-20504 {
    color: $c-dark-7;
  }

  .stripe-payment-element__express-label-20504 {
    background: $c-black-7;
  }

  .stripe-payment-element__card-divider-20504 {
    &::before,
    &::after {
      border-top-color: $c-dark-5;
    }
  }
}
</style>
